/**
 * Media Cache Service - Manages caching of media blobs in IndexedDB
 *
 * This service provides a simple interface to store and retrieve media blobs
 * using their MXC URL as the key. It helps avoid re-downloading attachments
 * that have already been viewed.
 */

import logger from '../utils/logger';

const DB_NAME = 'ti-m-media-cache';
const DB_VERSION = 2; // Incremented to add roomId index
const STORE_NAME = 'media_blobs';

class MediaCacheService {
  constructor() {
    this.dbPromise = null;
    this.init();
  }

  /**
   * Initialize the IndexedDB database and object store.
   */
  init() {
    if (typeof window === 'undefined' || !window.indexedDB) {
      logger.warn('IndexedDB is not supported in this browser. Media caching will be disabled.');
      return;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        logger.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME);
          // Add an index on roomId to efficiently find all media for a room.
          store.createIndex('roomId', 'roomId', { unique: false });
        } else if (event.oldVersion < 2) {
          const transaction = request.transaction;
          transaction.objectStore(STORE_NAME).createIndex('roomId', 'roomId', { unique: false });
        }
      };
    });
  }

  /**
   * Get a media blob from the cache.
   * @param {string} mxcUrl - The MXC URL of the media.
   * @returns {Promise<Blob|null>} The cached blob, or null if not found.
   */
  async getMedia(mxcUrl) {
    if (!this.dbPromise) return null;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(mxcUrl);

      request.onsuccess = () => {
        // The store now holds an object { blob, roomId }. Return just the blob.
        resolve(request.result?.blob || null);
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Store a media blob in the cache.
   * @param {string} mxcUrl - The MXC URL of the media.
   * @param {Blob} blob - The media blob to store.
   * @param {string} [roomId] - The optional room ID to associate with the media.
   * @returns {Promise<void>}
   */
  async setMedia(mxcUrl, blob, roomId) {
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      // Store an object containing the blob and the roomId.
      const dataToStore = { blob, roomId };
      const request = store.put(dataToStore, mxcUrl);

      request.onsuccess = () => resolve();
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Remove a single media item from the cache.
   * @param {string} mxcUrl - The MXC URL of the media to remove.
   * @returns {Promise<void>}
   */
  async removeMedia(mxcUrl) {
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(mxcUrl);

      request.onsuccess = () => {
        logger.debug(`[MediaCache] Removed media from cache: ${mxcUrl}`);
        resolve();
      };
      request.onerror = (event) => reject(event.target.error);
    });
  }

  /**
   * Remove all cached media associated with a specific room.
   * @param {string} roomId - The ID of the room to clear from the cache.
   * @returns {Promise<void>}
   */
  async removeMediaForRoom(roomId) {
    if (!this.dbPromise) return;
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('roomId');
      const request = index.openCursor(IDBKeyRange.only(roomId));

      let deleteCount = 0;
      let failCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const deleteRequest = store.delete(cursor.primaryKey);

          deleteRequest.onsuccess = () => {
            deleteCount++;
            cursor.continue();
          };

          deleteRequest.onerror = (e) => {
            failCount++;
            logger.error(`Failed to delete media ${cursor.primaryKey}:`, e.target.error);
            cursor.continue(); // Continue despite error
          };
        } else {
          // All cursor iterations complete
          if (failCount > 0) {
            logger.warn(`[MediaCache] Removed ${deleteCount} media items for room: ${roomId}, ${failCount} failed`);
          } else {
            logger.debug(`[MediaCache] Removed ${deleteCount} media items for room: ${roomId}`);
          }
          resolve({ deleted: deleteCount, failed: failCount });
        }
      };
      request.onerror = (event) => reject(event.target.error);

      // Optional: Handle transaction errors
      transaction.onerror = (event) => {
        logger.error('Transaction error during media removal:', event.target.error);
        reject(event.target.error);
      };
    });
  }
}

// Create a singleton instance
const mediaCacheService = new MediaCacheService();
export default mediaCacheService;
